import { Link } from 'react-router-dom';

export default function PrivacyPage() {
  return (
    <div className="max-w-2xl mx-auto py-8">
      <h1 className="text-2xl font-bold mb-6">Privacy Policy</h1>
      <hr className="mb-6" />

      <p className="text-sm text-muted mb-4">Last updated: February 10, 2026</p>

      <div className="space-y-6 text-sm leading-relaxed">
        <section>
          <h2 className="text-lg font-bold mb-2">1. Information We Collect</h2>
          <p>When you register for an account, we collect:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li><strong>Email address</strong> — used for account verification and service notifications</li>
            <li><strong>Username</strong> — used as your public display name</li>
            <li><strong>Password</strong> — stored securely using bcrypt hashing (we never store plaintext passwords)</li>
          </ul>
          <p className="mt-2">When you use the Service, we may also collect:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li><strong>IP address</strong> — for security, rate limiting, and analytics</li>
            <li><strong>Browser information</strong> — user agent string for analytics</li>
            <li><strong>Page views</strong> — anonymous usage statistics</li>
            <li><strong>Cookies</strong> — for authentication and preferences (see Section 5)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">2. How We Use Your Information</h2>
          <p>We use the collected information to:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Verify your email address and create your account</li>
            <li>Authenticate you when you sign in</li>
            <li>Send transactional emails (verification codes, account notifications)</li>
            <li>Prevent abuse and enforce rate limits</li>
            <li>Improve the Service through anonymous usage analytics</li>
          </ul>
          <p className="mt-2">
            We do <strong>not</strong> sell, rent, or share your personal information with third
            parties for marketing purposes.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">3. Email Communications</h2>
          <p>We send the following types of emails:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li><strong>Verification emails</strong> — one-time codes to confirm your email address</li>
            <li><strong>Account notifications</strong> — important changes to your account or the Service</li>
          </ul>
          <p className="mt-2">
            We do not send marketing or promotional emails. All emails are transactional and
            necessary for the operation of your account. You can stop receiving emails by
            deleting your account.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">4. Data Storage and Security</h2>
          <p>
            Your data is stored securely. Passwords are hashed using bcrypt. We use HTTPS
            for all communications. Access to user data is restricted to authorized
            administrators only.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">5. Cookies</h2>
          <p>We use cookies and local storage for:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li><strong>Authentication</strong> — JWT token to keep you signed in</li>
            <li><strong>Preferences</strong> — theme selection and cookie consent</li>
          </ul>
          <p className="mt-2">
            We do not use third-party tracking cookies.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">6. Third-Party Services</h2>
          <p>We use the following third-party services to operate:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li><strong>SendPulse</strong> — transactional email delivery</li>
            <li><strong>Resend</strong> — fallback email delivery</li>
          </ul>
          <p className="mt-2">
            These services process your email address solely for the purpose of delivering
            emails on our behalf. They are bound by their own privacy policies.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">7. Data Retention</h2>
          <p>
            We retain your account data for as long as your account is active. If you request
            account deletion, we will remove your personal data within 30 days.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">8. Your Rights</h2>
          <p>You have the right to:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your account and data</li>
            <li>Withdraw consent for email communications (by deleting your account)</li>
          </ul>
          <p className="mt-2">
            To exercise these rights, contact us at{' '}
            <a href="mailto:admin@codder.cc">admin@codder.cc</a>.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">9. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify registered
            users of significant changes via email or a notice on the Service.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-bold mb-2">10. Contact</h2>
          <p>
            For privacy-related inquiries, contact us at{' '}
            <a href="mailto:admin@codder.cc">admin@codder.cc</a>.
          </p>
        </section>
      </div>

      <hr className="my-6" />
      <p className="text-sm text-muted">
        <Link to="/register">Back to Registration</Link>
        {' | '}
        <Link to="/terms">Terms of Service</Link>
      </p>
    </div>
  );
}
